import { ThemeEvents } from '@theme/events';

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    // Capture phase: must run before bubble reaches quick-add-dialog (it closes on cart:update and clears stay-visible).
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddFromRecommendations, { capture: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAddFromRecommendations, { capture: true });
  }

  /**
   * Opens the cart drawer when add-to-cart succeeds from a product card in this block.
   * (Theme setting auto_open_cart_drawer may be off; quick-add modal renders the form outside this element.)
   */
  #handleCartAddFromRecommendations = (/** @type {Event} */ event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.source !== 'product-form-component' || detail?.data?.didError) return;

    const target = event.target;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    // Do not rely on event.target alone: shadow / retargeting / browser quirks can break contains().
    const inThisSection =
      (target instanceof Node && this.contains(target)) ||
      path.some((n) => n instanceof Node && n.nodeType === Node.ELEMENT_NODE && this.contains(n));

    // Modal form is outside this element; match productId to a card in this block only when Quick Add
    // is involved — avoids opening the drawer on main PDP add when that product also appears as a card.
    const productId = detail?.data?.productId;
    const byProductId =
      productId != null &&
      !!this.querySelector(`[data-product-id="${String(productId)}"]`);
    const quickAddModalActive =
      path.some((n) => n instanceof Element && n.id === 'quick-add-modal-content') ||
      !!this.querySelector('quick-add-component[stay-visible]');

    if (inThisSection || (byProductId && quickAddModalActive)) {
      this.#openCartDrawer();
      return;
    }

    const formRoot = target instanceof Element ? target.closest('product-form-component') : null;
    if (!formRoot?.closest('#quick-add-modal-content')) return;

    if (this.querySelector('quick-add-component[stay-visible]')) {
      this.#openCartDrawer();
    }
  };

  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'open' in drawer && typeof /** @type {{ open?: () => void }} */ (drawer).open === 'function') {
      /** @type {{ open: () => void }} */ (drawer).open();
    }
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
